import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, ShieldCheck } from 'lucide-react';

const lastUpdated = '31 May 2026';

type Certification = {
  name: string;
  detail: string;
};

type Provider = {
  id: string;
  name: string;
  role: string;
  trustUrl: string;
  trustLabel: string;
  summary: string;
  certifications: Certification[];
};

const providers: Provider[] = [
  {
    id: 'cloudflare',
    name: 'Cloudflare',
    role: 'Edge network, DDoS protection, TLS termination, and web application firewall',
    trustUrl: 'https://www.cloudflare.com/trust-hub/compliance-resources/',
    trustLabel: 'Cloudflare Trust Hub — compliance resources',
    summary:
      'Cloudflare operates a globally distributed network used to protect and accelerate the TS1 platform. Their security programme is independently audited against widely recognised standards.',
    certifications: [
      {
        name: 'ISO/IEC 27001',
        detail: 'Certified information security management system (ISMS). Cloudflare has held ISO 27001 since 2019.',
      },
      {
        name: 'ISO/IEC 27701',
        detail: 'Privacy information management extension to ISO 27001, supporting GDPR-aligned privacy controls.',
      },
      {
        name: 'SOC 2 Type II',
        detail: 'Independent attestation covering Security, Confidentiality, and Availability trust principles, audited annually.',
      },
      {
        name: 'PCI DSS Level 1',
        detail: 'Qualified Security Assessor (QSA) audit as both a Level 1 Service Provider and merchant for cardholder data environments.',
      },
      {
        name: 'GDPR & privacy frameworks',
        detail: 'Documented data protection practices, DPA availability, and participation in recognised international data transfer frameworks.',
      },
    ],
  },
  {
    id: 'railway',
    name: 'Railway',
    role: 'Application hosting, container runtime, and managed infrastructure for TS1 backend services',
    trustUrl: 'https://trust.railway.com/',
    trustLabel: 'Railway Trust Center',
    summary:
      'Railway hosts TS1 application workloads. Audit reports, penetration test summaries, and regulatory documentation are published or available on request through their Trust Center.',
    certifications: [
      {
        name: 'SOC 2 Type II',
        detail: 'Independent audit of security, availability, and confidentiality controls over a defined observation period.',
      },
      {
        name: 'SOC 3',
        detail: 'Public summary report derived from SOC 2 controls, suitable for high-level assurance reviews.',
      },
      {
        name: 'HIPAA (BAA available)',
        detail: 'Business Associate Agreement available for workloads handling protected health information under a shared responsibility model.',
      },
      {
        name: 'GDPR — Data Processing Agreement',
        detail: 'Standard DPA available for EU/UK data processing relationships (self-service at railway.com/legal/dpa).',
      },
      {
        name: 'EU-US & Swiss-US Data Privacy Framework',
        detail: 'Documented participation for lawful international transfers of personal data to the United States.',
      },
      {
        name: 'EU DORA documentation',
        detail: 'Disaster recovery, uptime, and IT control documentation available to regulated financial organisations under NDA via the Trust Center.',
      },
    ],
  },
  {
    id: 'supabase',
    name: 'Supabase',
    role: 'Managed PostgreSQL database, authentication, and object storage for TS1 platform data',
    trustUrl: 'https://supabase.com/security',
    trustLabel: 'Supabase Security',
    summary:
      'Supabase provides the managed data layer for TS1. Customer data is encrypted at rest and in transit; formal compliance reports are available to eligible customers through the Supabase dashboard.',
    certifications: [
      {
        name: 'ISO 27001',
        detail: 'Certified information security management system confirming rigorous controls for customer data protection.',
      },
      {
        name: 'SOC 2 Type II',
        detail: 'Independent attestation of security controls for the hosted platform, available to Team and Enterprise customers.',
      },
      {
        name: 'HIPAA (BAA available)',
        detail: 'HIPAA-compliant hosting available with a signed Business Associate Agreement on eligible plans.',
      },
      {
        name: 'Encryption & access controls',
        detail: 'AES-256 encryption at rest, TLS in transit, role-based access control, and daily backups on paid plans.',
      },
      {
        name: 'Vulnerability management',
        detail: 'Regular penetration testing and continuous security scanning through qualified third parties.',
      },
    ],
  },
];

function CertificationList({ items }: { items: Certification[] }) {
  return (
    <ul className="space-y-3">
      {items.map((cert) => (
        <li key={cert.name} className="flex gap-3">
          <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400 mt-0.5" aria-hidden />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{cert.name}</p>
            <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">{cert.detail}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function CompliancePage() {
  return (
    <div className="min-h-screen bg-app-canvas dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <Link
          to="/login"
          className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to sign in
        </Link>

        <header className="mb-10 pb-8 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
            Security &amp; Compliance
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Last updated: {lastUpdated}</p>
          <p className="mt-4 text-gray-600 dark:text-gray-300 leading-relaxed">
            This page summarises the independent security and compliance certifications held by the
            infrastructure providers that underpin the TS1 monitoring platform. It is intended to support
            procurement, information governance, and security reviews — particularly for public sector and
            regulated organisations evaluating Thermal Systems Ltd as a supplier.
          </p>
        </header>

        <section className="mb-10 p-5 rounded-lg bg-app-panel dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Our infrastructure stack</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            TS1 is built on a modern cloud-native stack. We select sub-processors that maintain formal,
            independently audited security programmes. Thermal Systems Ltd remains the data controller for
            customer account and monitoring data; the providers below process data solely to deliver hosting,
            networking, and storage services on our instructions, as described in our{' '}
            <Link to="/dpa" className="text-indigo-600 dark:text-indigo-400 hover:underline">
              Data Processing Agreement
            </Link>
            .
          </p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-600">
                  <th className="py-2 pr-4 font-semibold text-gray-900 dark:text-white">Provider</th>
                  <th className="py-2 pr-4 font-semibold text-gray-900 dark:text-white">Function</th>
                  <th className="py-2 font-semibold text-gray-900 dark:text-white">Key standards</th>
                </tr>
              </thead>
              <tbody className="text-gray-600 dark:text-gray-400">
                <tr className="border-b border-gray-100 dark:border-gray-700/80">
                  <td className="py-2.5 pr-4 font-medium text-gray-900 dark:text-white">Cloudflare</td>
                  <td className="py-2.5 pr-4">Edge security &amp; CDN</td>
                  <td className="py-2.5">ISO 27001, ISO 27701, SOC 2 Type II, PCI DSS</td>
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-700/80">
                  <td className="py-2.5 pr-4 font-medium text-gray-900 dark:text-white">Railway</td>
                  <td className="py-2.5 pr-4">Application hosting</td>
                  <td className="py-2.5">SOC 2 Type II, SOC 3, GDPR DPA, HIPAA BAA</td>
                </tr>
                <tr>
                  <td className="py-2.5 pr-4 font-medium text-gray-900 dark:text-white">Supabase</td>
                  <td className="py-2.5 pr-4">Database &amp; auth</td>
                  <td className="py-2.5">ISO 27001, SOC 2 Type II, HIPAA BAA</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <nav className="mb-8 p-4 rounded-lg bg-app-panel dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Provider details</h2>
          <ul className="text-sm space-y-1 text-indigo-600 dark:text-indigo-400">
            {providers.map((p) => (
              <li key={p.id}>
                <a href={`#${p.id}`} className="hover:underline">
                  {p.name}
                </a>
              </li>
            ))}
            <li>
              <a href="#requesting-reports" className="hover:underline">
                Requesting full audit reports
              </a>
            </li>
          </ul>
        </nav>

        <div className="space-y-8">
          {providers.map((provider) => (
            <section
              key={provider.id}
              id={provider.id}
              className="p-6 rounded-xl bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{provider.name}</h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{provider.role}</p>
                </div>
                <a
                  href={provider.trustUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline shrink-0"
                >
                  {provider.trustLabel}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </a>
              </div>
              <p className="mb-5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{provider.summary}</p>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                Certifications &amp; attestations
              </h3>
              <CertificationList items={provider.certifications} />
            </section>
          ))}
        </div>

        <section id="requesting-reports" className="mt-10 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Requesting full audit reports</h2>
          <p>
            Detailed SOC reports, ISO certificates, and penetration test summaries are issued by each provider
            and are typically available under NDA or through their customer trust portals. For TS1-specific
            assurance — including our DPA, security questionnaire responses, or copies of sub-processor reports
            we hold on file — contact Thermal Systems Ltd via{' '}
            <a
              href="https://thermal-systems.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              thermal-systems.co.uk
            </a>{' '}
            or your account manager.
          </p>
          <p className="mt-3">
            Certification status can change as providers renew audits. This page reflects publicly documented
            programmes as of the date above; authoritative copies are always the provider&apos;s issued
            certificates and audit letters.
          </p>
        </section>

        <footer className="mt-14 pt-8 border-t border-gray-200 dark:border-gray-700 text-center text-sm text-gray-500 dark:text-gray-400">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-3">
            <Link to="/terms" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Terms of Service
            </Link>
            <Link to="/privacy" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Privacy Policy
            </Link>
            <Link to="/cookies" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Cookie Policy
            </Link>
            <Link to="/dpa" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Data Processing Agreement
            </Link>
          </div>
          <p>© {new Date().getFullYear()} Thermal Systems Ltd. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
